// The onboarding surface's pure helpers (surface 9, milestone 5 check 7). Mirrors
// apps/api/src/onboard/journey.ts for the shapes; the words are the page's.
import type { OnboardingState } from '../api.js';

export type Stage = 'intro' | 'connect' | 'parked' | 'loading' | 'drafts' | 'areas' | 'triage';

export interface ConnectorFact {
  id: string; kind: string; status: string; mandatory: boolean; externalAccount: string | null; failureMessage: string | null;
  failedAt: string | null; adminAlertedAt: string | null; hasBackfill: boolean; expectedItems: number | null; doneItems: number;
  startedAt: string | null; etaAt: string | null; completedAt: string | null;
  /**
   * U137: whether anything has actually come back from the other system. A connector row says the
   * person pressed a button; this says the source answered, and no screen may print the word
   * "connected" until it is true.
   */
  cameBack: boolean;
}

export interface Slice { from: string; expected: number | null; done: number; doneAt: string | null }
export interface ReadingPlan {
  startedAt: string; historyStart: string; windowFrom: string;
  sent: Slice; first: Slice; window: Slice; back: Slice & { startedAt: string | null };
}
export interface Journey {
  intro?: { version: string; acceptedAt: string | null; exempt: boolean };
  plan: ReadingPlan | null;
  windowTotal: number; windowLeft: number; windowUnderstood: boolean; minutesToGo: number | null;
  voiceMeasured: boolean; callsAnswer: 'fathom' | 'fathom_waiting' | 'granola' | 'granola_later' | 'held' | 'declared' | null; fathomHeld: number;
  stage: Stage; gated: boolean; mayEnterTriage: boolean; missing: string[]; notetaker: ConnectorFact | null;
  parked: { kind: string; message: string; since: string | null } | null; backfillTotal: boolean; profileConfirmed: boolean;
  corrections: number; personalStore: boolean; onboardingStage: string | null;
  mail: { status: string; expectedItems: number | null; doneItems: number; startedAt: string | null; etaAt: string | null; completedAt: string | null; cameBack: boolean } | null;
  optional: Array<{ kind: string; status: string }>; connectors: ConnectorFact[];
}

export const CORRECTIONS_NEEDED = 3;

const LABELS: Record<string, string> = {
  m365_mail: 'Email', m365_teams: 'Teams', m365_calendar: 'Calendar', fathom: 'Fathom', granola: 'Granola', jira: 'Jira', slack: 'Slack',
};
export const kindLabel = (kind: string): string => LABELS[kind] ?? kind;

export function durationWords(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 60_000) return 'a minute or so';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours >= 24) { const days = Math.floor(hours / 24); const h = hours % 24; return `${days} day${days === 1 ? '' : 's'}${h ? ` ${h} hour${h === 1 ? '' : 's'}` : ''}`; }
  return `${hours} hour${hours === 1 ? '' : 's'}${rest ? ` ${rest} min` : ''}`;
}

export const PRIVACY = "Until Cobrain has sorted your mail, only you can see it. After that, who may see what follows the company's monitoring policy.";

/** D88, D125: the one line under Connect, with the estimate from the real count once the worker has it. */
export function nextStepLine(j: Journey, _now = Date.now()): string {
  return `Cobrain reads six months of your sent mail, then the last two weeks of everything, then the rest of the month while you work.${j.minutesToGo == null ? '' : ` ${j.minutesToGo} minutes to go.`} ${PRIVACY}`;
}

/**
 * The mandatory source holding the person on the loading screen, in the words the screen uses,
 * or null when the mailbox is the only thing still arriving.
 *
 * U137, applied to the screen a person waits on rather than to the one they press. A notetaker
 * chosen with a mistyped address is written 'pending' and nothing ever asks it anything: no key
 * exists for that address, so the row never fails, never alerts an admin, and never completes.
 * `loadingState` reads the MAILBOX alone, so the person then sits in front of a finished mailbox
 * and a bar at 100 per cent, for ever, with nothing on the screen naming what is missing. Naming
 * it does not mend the address -- panel 3's own round trip does that -- but it stops the wait
 * being silent, which is the part that makes it unanswerable.
 */
export function waitingOn(j: Journey): string | null {
  if (j.parked) return null;
  const mailDone = j.mail === null || j.mail.completedAt !== null;
  if (!mailDone) return null;
  const behind = j.connectors.find((c) => c.mandatory && c.status !== 'failed' && !(c.status === 'ready' && (!c.hasBackfill || c.completedAt !== null)));
  return behind ? kindLabel(behind.kind) : null;
}

/** The loading screen as built (D125) takes the mailbox's numbers; a park carries the plain message and says the admin knows (D98). */
export function loadingState(j: Journey): OnboardingState {
  const m = j.mail;
  const base: OnboardingState = {
    status: 'backfilling', connectorId: null, connectedAt: null, expectedItems: m?.expectedItems ?? null, doneItems: m?.doneItems ?? 0,
    startedAt: m?.startedAt ?? null, etaAt: m?.etaAt ?? null, completedAt: m?.completedAt ?? null, failureMessage: null,
  };
  if (j.parked) return { ...base, status: 'failed', failureMessage: `${kindLabel(j.parked.kind)}: ${j.parked.message} Your admin has been told.` };
  return base;
}

/** Milestone 4's draft routes answer `{ draft }`; read the id and the English body without trusting more of the shape. */
export function draftOf(resp: unknown): { id: string; body: string } | null {
  const d = typeof resp === 'object' && resp !== null && 'draft' in resp ? (resp as { draft: unknown }).draft : resp;
  if (typeof d !== 'object' || d === null) return null;
  const r = d as Record<string, unknown>;
  const body = typeof r.bodyEn === 'string' ? r.bodyEn : typeof r.body_en === 'string' ? r.body_en : null;
  return typeof r.id === 'string' && body !== null ? { id: r.id, body } : null;
}

/**
 * "March 2019" -- how far back Cobrain read, as a person says it. Month and year only: the day
 * the oldest email happened to be sent is noise, and a full date invites the reader to check it.
 *
 * The month name comes from a table and never from Intl (apps/portal/src/day.ts's rule): a
 * locale that abbreviates differently turns one screen's spelling of a month into another's.
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'] as const;

export function backTo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'everything Cobrain could reach';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
